// src/pages/Onboarding.js - VERSIONE COMPLETA E FUNZIONANTE
// Con selezione agente OPZIONALE e messaggi di skip
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Grid,
  Avatar,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDomain, useVisualTheme, useBranding, useLocalization } from '../context/DomainContext';
import { BACKEND_URL } from '../config';

const Onboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    displayName: '',
    ageGroup: '',
    language: 'it-IT',
    selectedAgentId: '',
    preferences: {}
  });
  
  // Stato per la gestione degli agenti
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState('');
  
  const navigate = useNavigate();
  const { user, refreshProfile, profile } = useAuth();
  
  // Hook per Visual Theme
  const visualTheme = useVisualTheme();
  const { brandName, logoUrl, organizationName } = useBranding();
  const { getText } = useLocalization();
  const { config, loading: domainLoading } = useDomain();

  // Verifica autenticazione e redirect se onboarding non necessario
  useEffect(() => {
    console.log('[Onboarding] Component mounted:', {
      hasUser: !!user,
      hasProfile: !!profile,
      isOnboardingCompleted: profile?.is_onboarding_completed
    });

    if (!user) {
      console.log('[Onboarding] No user, redirecting to login');
      navigate('/login', { replace: true });
      return;
    }

    // Se l'onboarding è già completato, reindirizza alla dashboard
    if (profile?.is_onboarding_completed) {
      console.log('[Onboarding] Onboarding already completed, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [user, profile, navigate]);

  // Steps dell'onboarding
  const steps = [
    getText('onboarding_step_1_title', 'Benvenuto'),
    'Configurazione Profilo',
    'Preferenze',
    'Completamento'
  ];

  const ageGroups = [
    { value: '18-25', label: '18-25 anni' },
    { value: '26-35', label: '26-35 anni' },
    { value: '36-45', label: '36-45 anni' },
    { value: '46-55', label: '46-55 anni' },
    { value: '56+', label: '56+ anni' },
    { value: 'prefer_not_say', label: 'Preferisco non dirlo' }
  ];

  // Carica agenti disponibili (opzionale - mantenuto per compatibilità)
  const fetchAvailableAgents = useCallback(async () => {
    if (!user || !config?.project_id) return;
    
    setLoadingAgents(true);
    setAgentsError('');
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/agents/available?project_id=${config.project_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Domain-Name': window.location.hostname,
          'Content-Type': 'application/json'
        }
      });
      
      // ✅ Gestione errori specifici
      if (response.status === 403) {
        console.warn('[Onboarding] 403 Forbidden - User may not have access to agents yet');
        setAgentsError('Accesso agli agenti non disponibile. Puoi completare questo passaggio in seguito.');
        setAvailableAgents([]);
        return;
      }
      
      if (response.status === 404) {
        console.warn('[Onboarding] 404 Not Found - No agents configured');
        setAgentsError('Nessun agente configurato al momento.');
        setAvailableAgents([]);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Errore ${response.status}: ${response.statusText}`);
      }
      
      const agentsData = await response.json();
      console.log('[Onboarding] Available agents loaded:', agentsData);

      const normalizeAgents = (raw) => (raw || [])
        .map(a => ({
          agent_id: String(a.agent_id ?? a.id ?? a.agentId ?? ''),
          ux_name: a.ux_name ?? a.name ?? a.title ?? 'Agente',
          ux_avatar_url: a.ux_avatar_url ?? a.avatar_url ?? a.avatar ?? '',
          ux_description: a.ux_description ?? a.description ?? '',
          is_main_agent: Boolean(a.is_main_agent ?? a.recommended ?? a.default)
        }))
        .filter(a => a.agent_id);

      const normalized = normalizeAgents(agentsData);
      console.log('[Onboarding] Available agents loaded (normalized):', normalized);
      setAvailableAgents(normalized);
      
    } catch (err) {
      console.error('[Onboarding] Error loading agents:', err);
      setAgentsError('Errore nel caricamento degli agenti. Puoi saltare questo step.');
      setAvailableAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  }, [user, config?.project_id]);

  // Carica agenti quando arriviamo allo step delle preferenze
  useEffect(() => {
    if (activeStep === 2 && availableAgents.length === 0 && !loadingAgents) {
      fetchAvailableAgents();
    }
  }, [activeStep, availableAgents.length, loadingAgents]); // ✅ RIMOSSO fetchAvailableAgents per evitare loop

  // FUNZIONE HANDLECOMPLETE - CON RETRY ROBUSTO E GESTIONE ERRORI
  const handleComplete = async () => {
    setLoading(true);
    setError('');

    try {
      const token = await user.getIdToken();
      
      const onboardingPayload = {
        user_display_name: formData.displayName,
        user_age_group: formData.ageGroup,
        user_locale: formData.language,
        provider_metadata_json: {
          onboarding_completed_at: new Date().toISOString(),
          onboarding_language: formData.language,
          onboarding_preferences: formData.preferences,
          selected_agent_id: formData.selectedAgentId || null  // Può essere vuoto
        }
      };

      console.log('[Onboarding] Completing onboarding with payload:', onboardingPayload);
      
      // Chiamata all'endpoint di completamento onboarding
      const response = await fetch(`${BACKEND_URL}/api/user/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Domain-Name': window.location.hostname
        },
        body: JSON.stringify(onboardingPayload)
      });

      const responseText = await response.text();
      console.log('[Onboarding] Response status:', response.status);
      console.log('[Onboarding] Response text:', responseText);

      if (!response.ok) {
        let errorMessage = `Errore server: ${response.status}`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Parse della risposta
      let onboardingResult;
      try {
        onboardingResult = JSON.parse(responseText);
        console.log('[Onboarding] Onboarding completed successfully:', onboardingResult);
      } catch (e) {
        console.log('[Onboarding] Response is not JSON, continuing...');
        onboardingResult = { message: 'Onboarding completato' };
      }

      // Refresh del profilo con retry migliorato
      let refreshAttempts = 0;
      const maxRefreshAttempts = 5;
      let refreshedProfile = null;
      let onboardingFlagUpdated = false;

      console.log('[Onboarding] Starting profile refresh...');
      
      while (refreshAttempts < maxRefreshAttempts && !onboardingFlagUpdated) {
        try {
          refreshAttempts++;
          console.log(`[Onboarding] Profile refresh attempt ${refreshAttempts}/${maxRefreshAttempts}...`);
          
          // Refresh del profilo
          refreshedProfile = await refreshProfile();
          
          console.log('[Onboarding] Profile refreshed:', {
            attempt: refreshAttempts,
            is_onboarding_completed: refreshedProfile?.is_onboarding_completed,
            user_display_name: refreshedProfile?.user_display_name
          });

          // Verifica se il campo è stato aggiornato
          if (refreshedProfile?.is_onboarding_completed === true) {
            console.log('[Onboarding] ✅ is_onboarding_completed successfully updated to true!');
            onboardingFlagUpdated = true;
            break;
          }

          // Se non è ancora aggiornato, aspetta prima del prossimo tentativo
          if (refreshAttempts < maxRefreshAttempts) {
            console.log('[Onboarding] ⏳ Waiting 1 second before next refresh attempt...');
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

        } catch (refreshError) {
          console.error(`[Onboarding] Profile refresh attempt ${refreshAttempts} failed:`, refreshError);
          
          // Se non è l'ultimo tentativo, aspetta e riprova
          if (refreshAttempts < maxRefreshAttempts) {
            console.log('[Onboarding] ⏳ Waiting 1 second before retry...');
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      // Verifica finale
      if (!onboardingFlagUpdated) {
        console.warn('[Onboarding] ⚠️ is_onboarding_completed not updated after all retries');
        console.warn('[Onboarding] Proceeding to dashboard anyway - onboarding may need manual verification');
      }

      // Redirect alla dashboard
      console.log('[Onboarding] 🎉 Redirecting to dashboard...');
      navigate('/dashboard', { replace: true });

    } catch (err) {
      console.error('[Onboarding] ❌ Onboarding completion failed:', err);
      setError(err.message || 'Errore durante il completamento dell\'onboarding. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  // Handler per navigazione tra gli step
  const handleNext = () => {
    // Validazione per step specifici
    if (activeStep === 1 && !formData.displayName.trim()) {
      setError('Inserisci un nome da visualizzare prima di continuare.');
      return;
    }
    
    // Step 2 (Preferenze/Agenti) è sempre valido anche senza selezione
    
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  // Aggiornamento dei dati del form
  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Loading state per caricamento del dominio
  if (domainLoading) {
    return (
      <Container maxWidth="md">
        <Box 
          sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa'
          }}
        >
          <CircularProgress sx={{ color: visualTheme.secondary_colors?.accent || '#0d6efd' }} />
        </Box>
      </Container>
    );
  }

  // ========== STEP COMPONENTS ==========

  // STEP 1: Welcome
  const WelcomeStep = () => (
    <Box sx={{ textAlign: 'center' }}>
      {/* Logo del brand */}
      {logoUrl && (
        <Box sx={{ mb: 4 }}>
          <img 
            src={logoUrl} 
            alt={`${brandName} Logo`}
            style={{ 
              maxHeight: '120px', 
              maxWidth: '300px', 
              height: 'auto' 
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </Box>
      )}

      <Typography 
        variant="h3" 
        component="h1" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary || '#212529',
          fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
          mb: 3
        }}
      >
        {getText('onboarding_step_1_title', `Benvenuto/a in ${brandName || config?.project_name || 'la piattaforma'}!`)}
      </Typography>

      <Typography 
        variant="h6" 
        sx={{ 
          color: visualTheme.text_colors?.secondary || '#6c757d',
          mb: 4,
          maxWidth: '600px',
          mx: 'auto',
          lineHeight: 1.6
        }}
      >
        Siamo entusiasti di averti a bordo! Questo breve tour ti aiuterà a configurare il tuo profilo 
        e a scoprire come {brandName || config?.project_name || 'la piattaforma'} può supportarti nel raggiungere i tuoi obiettivi.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 4, maxWidth: '700px', mx: 'auto' }}>
        <Grid item xs={12} sm={4}>
          <Card 
            sx={{ 
              height: '100%',
              backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
              borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
            }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <PersonIcon 
                sx={{ 
                  fontSize: 48, 
                  color: visualTheme.secondary_colors?.accent || '#0d6efd',
                  mb: 2 
                }} 
              />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: visualTheme.text_colors?.primary,
                  mb: 1,
                  fontFamily: visualTheme.font_definitions?.primary_font
                }}
              >
                Personalizza
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: visualTheme.text_colors?.secondary }}
              >
                Configura il tuo profilo secondo le tue preferenze
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card 
            sx={{ 
              height: '100%',
              backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
              borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
            }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <SmartToyIcon 
                sx={{ 
                  fontSize: 48, 
                  color: visualTheme.secondary_colors?.accent || '#0d6efd',
                  mb: 2 
                }} 
              />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: visualTheme.text_colors?.primary,
                  mb: 1,
                  fontFamily: visualTheme.font_definitions?.primary_font
                }}
              >
                Esplora
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: visualTheme.text_colors?.secondary }}
              >
                Scopri gli agenti AI a tua disposizione
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card 
            sx={{ 
              height: '100%',
              backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
              borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
            }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 48, 
                  color: visualTheme.secondary_colors?.accent || '#28a745',
                  mb: 2 
                }} 
              />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: visualTheme.text_colors?.primary,
                  mb: 1,
                  fontFamily: visualTheme.font_definitions?.primary_font
                }}
              >
                Inizia
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: visualTheme.text_colors?.secondary }}
              >
                Accedi a tutte le funzionalità della piattaforma
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography 
        variant="body2" 
        sx={{ 
          mt: 4, 
          color: visualTheme.text_colors?.secondary,
          fontStyle: 'italic' 
        }}
      >
        Ci vorranno solo 2-3 minuti!
      </Typography>
    </Box>
  );

  // STEP 2: Profile Configuration
  const ProfileStep = () => (
    <Box>
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary,
          fontFamily: visualTheme.font_definitions?.primary_font,
          mb: 3,
          textAlign: 'center'
        }}
      >
        Configura il tuo Profilo
      </Typography>

      <Box sx={{ maxWidth: '500px', mx: 'auto' }}>
        <TextField
          fullWidth
          label="Nome da visualizzare"
          value={formData.displayName}
          onChange={(e) => updateFormData('displayName', e.target.value)}
          placeholder="Come vuoi essere chiamato/a?"
          required
          sx={{ 
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
              '&.Mui-focused fieldset': {
                borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: visualTheme.secondary_colors?.accent || '#0d6efd',
            }
          }}
          helperText="Questo nome sarà visibile agli altri utenti e agli agenti"
        />

        <FormControl 
          fullWidth 
          sx={{ 
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
            }
          }}
        >
          <InputLabel>Fascia d'età</InputLabel>
          <Select
            value={formData.ageGroup}
            onChange={(e) => updateFormData('ageGroup', e.target.value)}
            label="Fascia d'età"
          >
            {ageGroups.map((group) => (
              <MenuItem key={group.value} value={group.value}>
                {group.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl 
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
            }
          }}
        >
          <InputLabel>Lingua preferita</InputLabel>
          <Select
            value={formData.language}
            onChange={(e) => updateFormData('language', e.target.value)}
            label="Lingua preferita"
          >
            <MenuItem value="it-IT">Italiano</MenuItem>
            <MenuItem value="en-US">English</MenuItem>
            <MenuItem value="es-ES">Español</MenuItem>
            <MenuItem value="fr-FR">Français</MenuItem>
            <MenuItem value="de-DE">Deutsch</MenuItem>
          </Select>
        </FormControl>

        <Alert 
          severity="info" 
          sx={{ 
            mt: 3,
            backgroundColor: visualTheme.primary_colors?.background || '#e7f3ff',
            borderRadius: visualTheme.component_styles?.button?.border_radius || '4px'
          }}
        >
          Queste informazioni ci aiutano a personalizzare la tua esperienza sulla piattaforma.
        </Alert>
      </Box>
    </Box>
  );

  // STEP 3: Preferences (Agent Selection) - DESIGN COMPATTO CON AVATAR PICCOLI
  const PreferencesStep = () => (
    <Box>
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary,
          fontFamily: visualTheme.font_definitions?.primary_font,
          mb: 2,
          textAlign: 'center',
          fontWeight: 600
        }}
      >
        Scegli il tuo Agente Principale
      </Typography>

      <Typography 
        variant="body1" 
        sx={{ 
          color: visualTheme.text_colors?.secondary,
          mb: 4,
          textAlign: 'center',
          maxWidth: '600px',
          mx: 'auto'
        }}
      >
        Seleziona l'agente AI che meglio si adatta alle tue esigenze, oppure salta questo passaggio e configuralo in seguito.
      </Typography>

      {/* ✅ PULSANTE SKIP IN CIMA */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={handleNext}
          startIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: visualTheme.component_styles?.button?.border_radius || '8px',
            borderColor: visualTheme.text_colors?.secondary || '#6c757d',
            color: visualTheme.text_colors?.secondary || '#6c757d',
            px: 4,
            py: 1.5,
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': {
              borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
              backgroundColor: `${visualTheme.secondary_colors?.accent || '#0d6efd'}10`,
            }
          }}
        >
          Salta questo passaggio
        </Button>
      </Box>

      {loadingAgents && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <CircularProgress sx={{ color: visualTheme.secondary_colors?.accent, mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Caricamento agenti disponibili...
          </Typography>
        </Box>
      )}

      {agentsError && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3, 
            maxWidth: '600px', 
            mx: 'auto',
            borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
          }}
        >
          {agentsError}
        </Alert>
      )}

      {!loadingAgents && availableAgents.length === 0 && !agentsError && (
        <Alert 
          severity="info" 
          sx={{ 
            maxWidth: '500px',
            mx: 'auto',
            backgroundColor: visualTheme.primary_colors?.background || '#e7f3ff',
            borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
          }}
        >
          Nessun agente disponibile al momento. Potrai configurarlo in seguito dal tuo profilo.
        </Alert>
      )}

      {!loadingAgents && availableAgents.length > 0 && (
        <>
          <Grid container spacing={2} sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}>
            {availableAgents.map((agent) => {
              const isSelected = formData.selectedAgentId === agent.agent_id;
              const isRecommended = agent.is_main_agent;
              
              return (
                <Grid item xs={12} sm={6} key={agent.agent_id}>
                  <Card 
                    onClick={() => updateFormData('selectedAgentId', agent.agent_id)}
                    sx={{ 
                      cursor: 'pointer',
                      position: 'relative',
                      border: isSelected 
                        ? `2px solid ${visualTheme.secondary_colors?.accent || '#0d6efd'}`
                        : '1px solid #dee2e6',
                      backgroundColor: isSelected
                        ? `${visualTheme.secondary_colors?.accent || '#0d6efd'}08`
                        : '#ffffff',
                      boxShadow: isSelected 
                        ? `0 4px 16px ${visualTheme.secondary_colors?.accent || '#0d6efd'}30`
                        : '0 1px 4px rgba(0,0,0,0.08)',
                      transition: 'all 0.2s ease',
                      borderRadius: visualTheme.component_styles?.button?.border_radius || '8px',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: isSelected
                          ? `0 6px 20px ${visualTheme.secondary_colors?.accent || '#0d6efd'}40`
                          : '0 4px 12px rgba(0,0,0,0.15)',
                        borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                      }
                    }}
                  >
                    {/* Checkmark se selezionato */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                          borderRadius: '50%',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      >
                        <CheckCircleIcon sx={{ color: '#ffffff', fontSize: 16 }} />
                      </Box>
                    )}

                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        {/* Avatar piccolo a sinistra */}
                        <Avatar 
                          src={agent.ux_avatar_url}
                          sx={{ 
                            width: 48, 
                            height: 48,
                            flexShrink: 0,
                            backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                            fontSize: '1.25rem',
                            fontWeight: 600
                          }}
                        >
                          {!agent.ux_avatar_url && (agent.ux_name?.charAt(0) || <SmartToyIcon sx={{ fontSize: 24 }} />)}
                        </Avatar>

                        {/* Contenuto a destra */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Nome e Badge */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography 
                              variant="h6"
                              sx={{ 
                                color: visualTheme.text_colors?.primary,
                                fontFamily: visualTheme.font_definitions?.primary_font,
                                fontWeight: 600,
                                fontSize: '1rem',
                                lineHeight: 1.3
                              }}
                            >
                              {agent.ux_name}
                            </Typography>
                            {isRecommended && (
                              <Chip 
                                label="★" 
                                size="small"
                                sx={{ 
                                  height: 20,
                                  minWidth: 20,
                                  padding: 0,
                                  backgroundColor: '#10b981',
                                  color: '#ffffff',
                                  fontSize: '0.75rem',
                                  '& .MuiChip-label': {
                                    padding: '0 4px'
                                  }
                                }}
                              />
                            )}
                          </Box>

                          {/* Descrizione */}
                          {agent.ux_description && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: visualTheme.text_colors?.secondary,
                                lineHeight: 1.5,
                                fontSize: '0.875rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {agent.ux_description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* ✅ PULSANTE SKIP IN FONDO */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleNext}
              startIcon={<ArrowForwardIcon />}
              sx={{
                borderRadius: visualTheme.component_styles?.button?.border_radius || '8px',
                borderColor: visualTheme.text_colors?.secondary || '#6c757d',
                color: visualTheme.text_colors?.secondary || '#6c757d',
                px: 4,
                py: 1.5,
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                  backgroundColor: `${visualTheme.secondary_colors?.accent || '#0d6efd'}10`,
                }
              }}
            >
              Salta questo passaggio
            </Button>
          </Box>

          {/* Messaggio informativo bottom */}
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 3,
              textAlign: 'center',
              color: visualTheme.text_colors?.secondary,
              fontStyle: 'italic',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Potrai sempre cambiare o aggiungere altri agenti dalla dashboard
          </Typography>
        </>
      )}
    </Box>
  );

  // STEP 4: Completion
  const CompletionStep = () => (
    <Box sx={{ textAlign: 'center' }}>
      <CheckCircleIcon 
        sx={{ 
          fontSize: 80, 
          color: visualTheme.secondary_colors?.accent || '#28a745',
          mb: 3 
        }} 
      />

      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary,
          fontFamily: visualTheme.font_definitions?.primary_font,
          mb: 2
        }}
      >
        Tutto Pronto!
      </Typography>

      <Typography 
        variant="body1" 
        sx={{ 
          color: visualTheme.text_colors?.secondary,
          mb: 4,
          maxWidth: '500px',
          mx: 'auto'
        }}
      >
        Il tuo profilo è stato configurato con successo. Sei pronto/a per iniziare a 
        esplorare tutte le funzionalità di {brandName || config?.project_name || 'la nostra piattaforma'}.
      </Typography>

      {/* Mostra l'agente selezionato se presente */}
      {formData.selectedAgentId && availableAgents.length > 0 && (
        <Card 
          sx={{ 
            maxWidth: 400, 
            mx: 'auto',
            mb: 4,
            backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
            borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
          }}
        >
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                color: visualTheme.secondary_colors?.accent || '#0d6efd',
                mb: 2
              }}
            >
              Il tuo Agente Principale:
            </Typography>
            {(() => {
              const selectedAgent = availableAgents.find(a => String(a.agent_id) === String(formData.selectedAgentId));
              return selectedAgent ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Avatar 
                    src={selectedAgent.ux_avatar_url}
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      mr: 2,
                      backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd'
                    }}
                  >
                    {selectedAgent.ux_name?.charAt(0) || <SmartToyIcon />}
                  </Avatar>
                  <Typography variant="body1" sx={{ color: visualTheme.text_colors?.primary }}>
                    {selectedAgent.ux_name || 'Agente Selezionato'}
                  </Typography>
                </Box>
              ) : null;
            })()}
          </CardContent>
        </Card>
      )}

      {/* Messaggio se nessun agente selezionato */}
      {!formData.selectedAgentId && (
        <Alert 
          severity="info" 
          sx={{ 
            maxWidth: 400, 
            mx: 'auto',
            mb: 4,
            backgroundColor: visualTheme.primary_colors?.background || '#e7f3ff'
          }}
        >
          Potrai configurare il tuo agente principale in qualsiasi momento dalla dashboard.
        </Alert>
      )}

      <Card 
        sx={{ 
          maxWidth: 400, 
          mx: 'auto',
          backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
          borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
        }}
      >
        <CardContent>
          <Typography 
            variant="h6" 
            sx={{ 
              color: visualTheme.secondary_colors?.accent || '#0d6efd',
              mb: 2
            }}
          >
            I tuoi prossimi passi:
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Esplora la dashboard principale
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Inizia una conversazione con il tuo agente
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Crea il tuo primo contenuto
            </Typography>
            <Typography variant="body2" sx={{ color: visualTheme.text_colors?.secondary }}>
              • Scopri tutte le funzionalità disponibili
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  // Render dello step corrente
  const renderStepContent = (step) => {
    switch (step) {
      case 0: return <WelcomeStep />;
      case 1: return <ProfileStep />;
      case 2: return <PreferencesStep />;
      case 3: return <CompletionStep />;
      default: return null;
    }
  };

  // ========== MAIN RENDER ==========
  return (
    <Container maxWidth="md">
      <Box 
        sx={{ 
          minHeight: '100vh', 
          py: 4,
          backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa'
        }}
      >
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4,
            backgroundColor: visualTheme.primary_colors?.header || '#ffffff',
            borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
          }}
        >
          {/* Progress Stepper */}
          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mb: 4,
              '& .MuiStepIcon-root.Mui-active': {
                color: visualTheme.secondary_colors?.accent || '#0d6efd',
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: visualTheme.secondary_colors?.accent || '#28a745',
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{
                  '& .MuiStepLabel-label': {
                    color: visualTheme.text_colors?.secondary || '#6c757d',
                    fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif'
                  }
                }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Progress bar */}
          <LinearProgress 
            variant="determinate" 
            value={(activeStep / (steps.length - 1)) * 100} 
            sx={{ 
              mb: 4,
              '& .MuiLinearProgress-bar': {
                backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd'
              }
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          <Box sx={{ minHeight: '400px', mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ 
                color: visualTheme.text_colors?.secondary || '#6c757d',
                fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif'
              }}
            >
              Indietro
            </Button>

            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleComplete}
                  disabled={loading}
                  sx={{ 
                    backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                    borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
                    fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
                    '&:hover': {
                      backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                      opacity: 0.9
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    getText('button_finish_onboarding', 'Inizia il tuo Viaggio')
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ 
                    backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                    borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
                    fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
                    '&:hover': {
                      backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                      opacity: 0.9
                    }
                  }}
                >
                  {getText('button_next', 'Avanti')}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Onboarding;